/**
 * Admin authentication via magic link (ADR 014, replaces §59 passwords).
 *
 * Flow: POST /login (always 202) → email with `${ADMIN_BASE_URL}/login/verify#t=<token>` →
 * SPA reads the fragment, removes it, POSTs /verify → session cookie `__Host-lsc_admin`
 * (HttpOnly) + CSRF cookie `__Host-lsc_csrf` (readable) + `csrfToken` in the body.
 * All mutating admin requests send `X-CSRF-Token`.
 */
import { z } from 'zod';
import { AuthProviderSchema } from '../enums';
import {
  DisplayNameSchema,
  EmailInputSchema,
  IdSchema,
  IsoDateTimeSchema,
  OpaqueTokenSchema,
} from './common';

/** §148 – provider-independent admin identity used by the domain. */
export const AdminIdentitySchema = z
  .object({
    id: IdSchema,
    email: z.email(),
    displayName: z.string(),
    authProvider: AuthProviderSchema,
  })
  .strict();
export type AdminIdentity = z.infer<typeof AdminIdentitySchema>;

export const AdminSessionInfoSchema = z
  .object({
    /** Sliding expiry (idle timeout). */
    expiresAt: IsoDateTimeSchema,
    /** Absolute expiry; the session can never live longer. */
    absoluteExpiresAt: IsoDateTimeSchema,
  })
  .strict();
export type AdminSessionInfo = z.infer<typeof AdminSessionInfoSchema>;

// POST /api/v1/admin/auth/login → 202, identical for known and unknown addresses.
export const AdminLoginRequestSchema = z
  .object({
    email: EmailInputSchema,
    turnstileToken: z.string().min(1).max(2048),
  })
  .strict();
export type AdminLoginRequest = z.input<typeof AdminLoginRequestSchema>;
export const AdminLoginResponseSchema = z.object({ accepted: z.literal(true) }).strict();
export type AdminLoginResponse = z.infer<typeof AdminLoginResponseSchema>;

// POST /api/v1/admin/auth/verify → 200 + cookies
export const AdminVerifyRequestSchema = z.object({ token: OpaqueTokenSchema }).strict();
export type AdminVerifyRequest = z.infer<typeof AdminVerifyRequestSchema>;
export const AdminVerifyResponseSchema = z
  .object({
    admin: AdminIdentitySchema,
    session: AdminSessionInfoSchema,
    /** Same value as the `__Host-lsc_csrf` cookie; send as `X-CSRF-Token`. */
    csrfToken: z.string(),
  })
  .strict();
export type AdminVerifyResponse = z.infer<typeof AdminVerifyResponseSchema>;

// POST /api/v1/admin/auth/logout → 204 (session revoked server-side, cookies cleared)

// GET /api/v1/admin/auth/me → 200 | 401 UNAUTHORIZED/SESSION_EXPIRED
export const AdminMeResponseSchema = z
  .object({
    admin: AdminIdentitySchema,
    session: AdminSessionInfoSchema,
  })
  .strict();
export type AdminMeResponse = z.infer<typeof AdminMeResponseSchema>;

// --- Admin users (GET/POST/PATCH /api/v1/admin/admin-users) ------------------------------------

export const AdminUserSchema = z
  .object({
    id: IdSchema,
    email: z.email(),
    displayName: z.string(),
    active: z.boolean(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    lastLoginAt: IsoDateTimeSchema.nullable(),
    deactivatedAt: IsoDateTimeSchema.nullable(),
  })
  .strict();
export type AdminUser = z.infer<typeof AdminUserSchema>;

export const AdminUserListResponseSchema = z.object({ items: z.array(AdminUserSchema) }).strict();
export type AdminUserListResponse = z.infer<typeof AdminUserListResponseSchema>;

export const CreateAdminUserRequestSchema = z
  .object({
    email: EmailInputSchema,
    displayName: DisplayNameSchema,
  })
  .strict();
export type CreateAdminUserRequest = z.input<typeof CreateAdminUserRequestSchema>;

/** Deactivation revokes all sessions of that admin; the last active admin cannot be deactivated. */
export const UpdateAdminUserRequestSchema = z
  .object({
    displayName: DisplayNameSchema.optional(),
    active: z.boolean().optional(),
  })
  .strict()
  .refine((v) => v.displayName !== undefined || v.active !== undefined, {
    message: 'Mindestens ein Feld angeben.',
  });
export type UpdateAdminUserRequest = z.input<typeof UpdateAdminUserRequestSchema>;
